import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { homepageTopicsQuery } from "@/api-queries/homepage-topics";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { Img } from "@/components/ui/img";
import { ArrowRight } from "@phosphor-icons/react";
import { defineSection } from "@/ssr/define-section";

// Mobile: uniform 2-col grid of 16:9 tiles.
// sm+: bento layout with 6 slots and deliberate asymmetry —
// hero spans 4×2, tall spans 2×2, then alternating wide/normal at 3×1.
const BENTO_CLASSES = [
  "col-span-1 sm:col-span-4 sm:row-span-2",
  "col-span-1 sm:col-span-2 sm:row-span-2",
  "col-span-1 sm:col-span-3 sm:row-span-1",
  "col-span-1 sm:col-span-3 sm:row-span-1",
  "col-span-1 sm:col-span-3 sm:row-span-1",
  "col-span-1 sm:col-span-3 sm:row-span-1",
];

function TopicsSectionView() {
  const ref = useScrollReveal<HTMLElement>();
  const { t } = useTranslation();

  const { data } = useQuery(homepageTopicsQuery);
  const topics = data?.topics ?? [];

  if (topics.length === 0) return null;

  const displayed = topics.slice(0, 6);

  return (
    <section
      ref={ref}
      id="teme"
      className="relative overflow-hidden py-24 sm:py-32"
      style={{ background: "oklch(100% 0 0)" }}
    >
      <div className="relative mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-3xl text-center">
          <h2
            className="font-black leading-[0.92] tracking-[-0.04em] text-foreground"
            style={{
              fontFamily: "var(--font-heading)",
              fontSize: "clamp(2.25rem, 4.5vw, 3.5rem)",
            }}
          >
            {t("home.topics.titlePrefix")}{" "}
            <em className="not-italic" style={{ color: "oklch(37.12% 0.059 186.62)" }}>
              {t("home.topics.titleHighlight")}
            </em>
            {t("home.topics.titleSuffix") ? <> {t("home.topics.titleSuffix")}</> : null}.
          </h2>
          <p className="mx-auto mt-6 max-w-md text-base leading-relaxed text-muted-foreground sm:text-lg">
            {t("home.topics.subtitle")}
          </p>
        </div>

        {/* Bento grid */}
        <div className="mt-14 grid grid-cols-2 gap-3 sm:auto-rows-[260px] sm:grid-cols-6 sm:gap-4">
          {displayed.map((topic, i) => {
            const isHero = i === 0;
            const cls = BENTO_CLASSES[i] ?? "col-span-1 sm:col-span-3 sm:row-span-1";

            return (
              <Link
                key={topic.tag}
                to="/therapists"
                search={{ problemTag: topic.tag }}
                className={`group relative aspect-[16/9] overflow-hidden rounded-3xl border bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl sm:aspect-auto ${cls}`}
                style={{ borderColor: "oklch(92% 0.02 84)" }}
              >
                <Img
                  src={topic.imageUrl}
                  alt={topic.label}
                  sizes={isHero ? "(min-width: 1024px) 66vw, 50vw" : "(min-width: 1024px) 33vw, 50vw"}
                  className="absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-105"
                />

                {/* Scrim */}
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, transparent 30%, oklch(15% 0.02 84 / 0.75) 100%)",
                  }}
                />

                {/* Content */}
                <div className="absolute inset-x-0 bottom-0 p-4 text-white sm:p-6">
                  <h3
                    className={`font-black leading-[1.05] tracking-[-0.01em] ${isHero ? "text-base sm:text-3xl lg:text-4xl" : "text-base sm:text-xl"}`}
                    style={{ fontFamily: "var(--font-heading)" }}
                  >
                    {topic.label}
                  </h3>
                  {isHero && topic.description && (
                    <p className="mt-2 hidden max-w-md text-sm leading-relaxed text-white/85 sm:block sm:text-base">
                      {topic.description}
                    </p>
                  )}
                </div>
              </Link>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-12 flex justify-center">
          <Link
            to="/therapists"
            className="inline-flex items-center gap-4 rounded-full px-8 text-xs font-bold uppercase tracking-[0.14em] transition-transform hover:scale-[1.02] h-14"
            style={{ background: "oklch(37.12% 0.059 186.62)", color: "oklch(98% 0 0)" }}
          >
            {t("home.topics.viewAll")}
            <ArrowRight className="size-4" weight="bold" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export const TopicsSection = defineSection({
  Component: TopicsSectionView,
  queries: [homepageTopicsQuery],
});
